import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { Eraser, X } from "lucide-react";
import { MAP_COLORS } from "@/lib/map-colors";
import { removeMapLayers } from "@/lib/map-cleanup";
import { AnimatePresence, motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useMarkStreetsIncomplete } from "@/api/orval";
import type { ManualMatchRequest } from "@/api/orval-types";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";

import type { FeatureCollection, StreetSegment } from "@/types/geojson";

interface StreetIncompleteBrushProps {
  map: maplibregl.Map | null;
  streets: FeatureCollection | undefined;
  active: boolean;
  onToggle: () => void;
  // When true the trigger button is rendered elsewhere (the bottom-left tools
  // control), so this component renders only its active-mode panel and logic.
  hideTrigger?: boolean;
}

// Mirror of StreetCompletionBrush, but erases coverage from matched streets.
// Uses its own map source/layer ids ("erase-*") so it never collides with the
// completion brush.
export const StreetIncompleteBrush = ({
  map,
  streets,
  active,
  onToggle,
  hideTrigger = false,
}: StreetIncompleteBrushProps) => {
  const [selectedSegments, setSelectedSegments] = useState<StreetSegment[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [brushRadiusMeters, setBrushRadiusMeters] = useState(15);
  const [previewSegments, setPreviewSegments] = useState<StreetSegment[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const brushCenter = useRef<[number, number] | null>(null);
  const currentSegmentsRef = useRef<StreetSegment[]>([]);
  const { workspaceId } = useCurrentWorkspace();
  const markStreetsMutation = useMarkStreetsIncomplete();
  const { toast } = useToast();

  const findSegmentsUnderBrush = (lng: number, lat: number) => {
    if (!map || !streets) return [];

    const segments: StreetSegment[] = [];
    const radiusDegrees = brushRadiusMeters / 111320;

    streets.features.forEach((feature) => {
      if (!feature.geometry) return;

      let coords: number[][][] = [];
      if (feature.geometry.type === "LineString") {
        coords = [feature.geometry.coordinates];
      } else if (feature.geometry.type === "MultiLineString") {
        coords = feature.geometry.coordinates;
      }

      coords.forEach((lineCoords) => {
        for (let i = 0; i < lineCoords.length - 1; i++) {
          const [lon1, lat1] = lineCoords[i];
          const [lon2, lat2] = lineCoords[i + 1];

          const segmentLength = Math.sqrt(
            Math.pow(lon2 - lon1, 2) + Math.pow(lat2 - lat1, 2),
          );
          const subdivisions = Math.max(
            1,
            Math.ceil(segmentLength / (radiusDegrees * 0.5)),
          );

          for (let j = 0; j < subdivisions; j++) {
            const t1 = j / subdivisions;
            const t2 = (j + 1) / subdivisions;

            const subLon1 = lon1 + (lon2 - lon1) * t1;
            const subLat1 = lat1 + (lat2 - lat1) * t1;
            const subLon2 = lon1 + (lon2 - lon1) * t2;
            const subLat2 = lat1 + (lat2 - lat1) * t2;

            const distance = pointToLineSegmentDistance(
              lng,
              lat,
              subLon1,
              subLat1,
              subLon2,
              subLat2,
            );

            if (distance <= radiusDegrees) {
              segments.push({
                street_id: feature.id,
                geometry: {
                  type: "LineString",
                  coordinates: [
                    [subLon1, subLat1],
                    [subLon2, subLat2],
                  ],
                },
              });
            }
          }
        }
      });
    });

    return segments;
  };

  const pointToLineSegmentDistance = (
    px: number,
    py: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ) => {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;

    if (lenSq !== 0) param = dot / lenSq;

    let xx, yy;

    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  };

  useEffect(() => {
    if (!map || !active || !streets) return;

    map.dragPan.disable();
    map.getCanvas().style.cursor = "crosshair";

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (!active) return;
      brushCenter.current = [e.lngLat.lng, e.lngLat.lat];

      const currentSegments = findSegmentsUnderBrush(
        e.lngLat.lng,
        e.lngLat.lat,
      );

      if (isDrawing) {
        setPreviewSegments([]);

        if (currentSegments.length > 0) {
          const combined = [...currentSegmentsRef.current, ...currentSegments];
          const unique = combined.filter(
            (seg, index, self) =>
              index ===
              self.findIndex(
                (s) =>
                  s.street_id === seg.street_id &&
                  JSON.stringify(s.geometry.coordinates) ===
                    JSON.stringify(seg.geometry.coordinates),
              ),
          );

          currentSegmentsRef.current = unique;

          const source = map.getSource(
            "erase-selected-segments",
          ) as maplibregl.GeoJSONSource;
          if (source) {
            const features = unique.map((seg, idx) => ({
              type: "Feature" as const,
              id: idx,
              geometry: seg.geometry,
              properties: {},
            }));
            source.setData({
              type: "FeatureCollection",
              features,
            });
          }

          setSelectedSegments(unique);
        }
      } else {
        setPreviewSegments(currentSegments);
      }

      if (map.getSource("erase-circle")) {
        const metersPerPixel =
          (156543.03392 * Math.cos((e.lngLat.lat * Math.PI) / 180)) /
          Math.pow(2, map.getZoom());
        const radiusPixels = brushRadiusMeters / metersPerPixel;

        (map.getSource("erase-circle") as maplibregl.GeoJSONSource).setData({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [e.lngLat.lng, e.lngLat.lat],
          },
          properties: { radius: radiusPixels },
        });
      }
    };

    const onMouseDown = (e: maplibregl.MapMouseEvent) => {
      if (!active) return;
      e.preventDefault();
      setIsDrawing(true);

      const newSegments = findSegmentsUnderBrush(e.lngLat.lng, e.lngLat.lat);

      if (newSegments.length > 0) {
        const combined = [...currentSegmentsRef.current, ...newSegments];
        const unique = combined.filter(
          (seg, index, self) =>
            index ===
            self.findIndex(
              (s) =>
                s.street_id === seg.street_id &&
                JSON.stringify(s.geometry.coordinates) ===
                  JSON.stringify(seg.geometry.coordinates),
            ),
        );
        currentSegmentsRef.current = unique;

        const source = map.getSource(
          "erase-selected-segments",
        ) as maplibregl.GeoJSONSource;
        if (source) {
          const features = unique.map((seg, idx) => ({
            type: "Feature" as const,
            id: idx,
            geometry: seg.geometry,
            properties: {},
          }));
          source.setData({
            type: "FeatureCollection",
            features,
          });
        }

        setSelectedSegments(unique);
      }
    };

    const onMouseUp = () => {
      if (!active) return;
      setIsDrawing(false);
    };

    map.on("mousemove", onMouseMove);
    map.on("mousedown", onMouseDown);
    map.on("mouseup", onMouseUp);

    return () => {
      map.off("mousemove", onMouseMove);
      map.off("mousedown", onMouseDown);
      map.off("mouseup", onMouseUp);
      map.dragPan.enable();
      map.getCanvas().style.cursor = "";
    };
  }, [map, active, streets, brushRadiusMeters, isDrawing]);

  // Brush visualization layers
  useEffect(() => {
    if (!map || !active) return;

    if (!map.getSource("erase-circle")) {
      map.addSource("erase-circle", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "erase-circle-fill",
        type: "circle",
        source: "erase-circle",
        paint: {
          "circle-radius": ["get", "radius"],
          "circle-color": MAP_COLORS.erase,
          "circle-opacity": 0.1,
          "circle-stroke-width": 2,
          "circle-stroke-color": MAP_COLORS.erase,
          "circle-stroke-opacity": 0.5,
        },
      });
    }

    if (!map.getSource("erase-preview")) {
      map.addSource("erase-preview", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "erase-preview-line",
        type: "line",
        source: "erase-preview",
        paint: {
          "line-color": MAP_COLORS.erasePreview,
          "line-width": 4,
          "line-opacity": 0.7,
        },
      });
    }

    return () =>
      removeMapLayers(
        map,
        ["erase-circle-fill", "erase-preview-line"],
        ["erase-circle", "erase-preview"],
      );
  }, [map, active]);

  // Live preview
  useEffect(() => {
    if (!map || !active) return;

    const source = map.getSource("erase-preview") as maplibregl.GeoJSONSource;
    if (source) {
      const previewFeatures = previewSegments.map((seg, idx) => ({
        type: "Feature" as const,
        id: idx,
        geometry: seg.geometry,
        properties: {},
      }));
      source.setData({
        type: "FeatureCollection",
        features: previewFeatures,
      });
    }
  }, [map, active, previewSegments]);

  // Selected segments layer (created once)
  useEffect(() => {
    if (!map || !active) return;

    if (!map.getSource("erase-selected-segments")) {
      map.addSource("erase-selected-segments", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "erase-selected-segments-line",
        type: "line",
        source: "erase-selected-segments",
        paint: {
          "line-color": MAP_COLORS.erase,
          "line-width": 6,
          "line-opacity": 1.0,
        },
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
      });
    }

    return () =>
      removeMapLayers(
        map,
        ["erase-selected-segments-line"],
        ["erase-selected-segments"],
      );
  }, [map, active]);

  // Update selected segments data
  useEffect(() => {
    if (!map || !active) return;

    const source = map.getSource(
      "erase-selected-segments",
    ) as maplibregl.GeoJSONSource;
    if (!source) return;

    const selectedFeatures = selectedSegments.map((seg, idx) => ({
      type: "Feature" as const,
      id: idx,
      geometry: seg.geometry,
      properties: {},
    }));

    source.setData({
      type: "FeatureCollection",
      features: selectedFeatures,
    });
  }, [map, active, selectedSegments]);

  const handleMarkIncomplete = async () => {
    try {
      await markStreetsMutation.mutateAsync({
        pathParams: { workspaceId: workspaceId ?? "" },
        data: {
          street_segments: selectedSegments,
        } as unknown as ManualMatchRequest,
      });
      toast({
        title: "Success",
        description: `Marked ${selectedSegments.length} street segment(s) incomplete`,
      });
      currentSegmentsRef.current = []; // reset so the next brush starts empty
      setSelectedSegments([]);
      setShowConfirmDialog(false);
      onToggle();
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to mark segments incomplete",
        variant: "destructive",
      });
    }
  };

  const handleClearSelection = () => {
    currentSegmentsRef.current = [];
    setSelectedSegments([]);
  };

  return (
    <>
      {/* Button row (right edge): route=182, complete=126, incomplete=70, delete=4 */}
      {!hideTrigger && (
        <div className="absolute right-[70px] top-4 z-10">
          <div className="rounded-md bg-white shadow">
            <Button
              variant={active ? "default" : "ghost"}
              className={
                active
                  ? "w-[50px] border-l bg-red-600 hover:bg-red-700"
                  : "w-[50px] border-l"
              }
              size="icon"
              onClick={onToggle}
              title={
                active ? "Exit incomplete mode" : "Mark streets incomplete"
              }
            >
              {active ? (
                <X className="h-4 w-4 text-white" />
              ) : (
                <Eraser className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {active && (
          <motion.div
            className="absolute bottom-4 left-14 z-10"
            initial={{ opacity: 0, scale: 0.96, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -8 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="w-[300px] rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-gray-900">
                    Incomplete Brush
                  </h3>
                </div>
                <button
                  onClick={onToggle}
                  className="text-gray-400 transition-colors hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {selectedSegments.length === 0 ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Click and drag over completed streets to mark them
                    incomplete
                  </p>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-700">
                        Brush Size
                      </label>
                      <span className="text-sm font-medium text-gray-900">
                        {brushRadiusMeters}m
                      </span>
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="50"
                      step="5"
                      value={brushRadiusMeters}
                      onChange={(e) =>
                        setBrushRadiusMeters(Number(e.target.value))
                      }
                      className="slider h-1 w-full cursor-pointer appearance-none rounded-lg bg-gray-200"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-md border border-red-200 bg-red-50 p-3">
                    <div className="text-sm font-medium text-red-900">
                      {selectedSegments.length} segment
                      {selectedSegments.length !== 1 ? "s" : ""} selected
                    </div>
                    <div className="mt-0.5 text-xs text-red-700">
                      Ready to mark incomplete
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearSelection}
                      className="flex-1 text-sm"
                    >
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setShowConfirmDialog(true)}
                      className="flex-1 bg-red-600 text-sm text-white hover:bg-red-700"
                      disabled={markStreetsMutation.isPending}
                    >
                      <Eraser className="mr-1 h-3.5 w-3.5" />
                      Mark Incomplete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Segments as Incomplete</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark {selectedSegments.length} street
              segment
              {selectedSegments.length !== 1 ? "s" : ""} incomplete? This
              removes their completion.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMarkIncomplete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Mark Incomplete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
